import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import type {
	AiUsageByFeatureModel,
	AiUsageBySurfaceModel,
} from '../dto/ai-usage.dto';
import { BaseEntity } from './base.entity';
import { decimalNumber } from './decimal-number.transformer';

/**
 * PD-16760: AI credits the whole tenant used in one period, rolled up from
 * `ai_usage_event`. One row per period.
 *
 * Written incrementally in the same transaction as each event and rebuilt
 * nightly from the events, exactly like `ai_usage_user_period`. Unlike that
 * table it counts every event, including system consumers that have no user.
 *
 * Not audited (`auditable: false`): every metered call rewrites the row.
 *
 * SM1 exposure: none. SM1 names no such table.
 */
@Entity('ai_usage_tenant_period', {}, { auditable: false })
@Index('uk_ai_usage_tenant_period_period', ['periodKey'], { unique: true })
export class AiUsageTenantPeriodEntity extends BaseEntity {
	/** `YYYY-MM`, see `aiUsagePeriodKey`. */
	@Column('char', { name: 'periodKey', length: 7 })
	public periodKey!: string;

	/**
	 * Snapshot of the credits the tenant has for the period. PD-16876: the
	 * monthly Google credit pool (credits per seat x seats counted), written
	 * with each Google charge; the latest known size wins. Null reads as "not
	 * recorded".
	 */
	@Column('decimal', {
		name: 'poolCredits',
		precision: 14,
		scale: 4,
		nullable: true,
		transformer: decimalNumber,
	})
	public poolCredits!: number | null;

	/** Credits paid from top-up packs (events with bucket `topup`). */
	@Column('decimal', {
		name: 'topupCreditsApplied',
		precision: 14,
		scale: 4,
		default: 0,
		transformer: decimalNumber,
	})
	public topupCreditsApplied!: number;

	/** Credits billed as overage (events with bucket `overage`). */
	@Column('decimal', {
		name: 'overageCredits',
		precision: 14,
		scale: 4,
		default: 0,
		transformer: decimalNumber,
	})
	public overageCredits!: number;

	/** Every credit used in the period, users and system together. */
	@Column('decimal', {
		name: 'creditsUsed',
		precision: 14,
		scale: 4,
		default: 0,
		transformer: decimalNumber,
	})
	public creditsUsed!: number;

	/** The part of `creditsUsed` with no user (system consumers). */
	@Column('decimal', {
		name: 'systemCreditsUsed',
		precision: 14,
		scale: 4,
		default: 0,
		transformer: decimalNumber,
	})
	public systemCreditsUsed!: number;

	@Column('int', { name: 'eventCount', unsigned: true, default: 0 })
	public eventCount!: number;

	/** Credits and events by surface. */
	@Column('json', { name: 'usageBySurface', nullable: true })
	public usageBySurface!: AiUsageBySurfaceModel | null;

	/** Credits and events by surface, then feature. */
	@Column('json', { name: 'usageByFeature', nullable: true })
	public usageByFeature!: AiUsageByFeatureModel | null;

	@Column('datetime', { name: 'lastActivityAt', precision: 3, nullable: true })
	public lastActivityAt!: Date | null;
}
